/* LOOKBOOK 26AW: 開幕の演出(落ちる/めくる/集まる)とスクロールで浮かぶ(sections/callec-lookbook.liquid)
   幕([data-clb-intro])は HTML に最初から入っていて読み込み中を隠す。2回目以降・動きを減らす設定・途中位置への復帰では
   セクション内のインラインスクリプトが描画前に幕を外す。ここが動かなくても幕は CSS で5秒後に消える */
(() => {
  const OUT = 'cubic-bezier(.16, 1, .3, 1)';      // 着地: 長く減速して止まる
  const INOUT = 'cubic-bezier(.65, 0, .35, 1)';   // 移動: 静かに出て静かに止まる
  const IN = 'cubic-bezier(.7, 0, .84, 0)';       // 退場: ゆっくり動き出して加速
  const LOAD_LIMIT = 3000;                        // 画像を待つ上限(ms)。超えたら読めた分だけで始める
  const ENOUGH = 6;                               // 表紙+この枚数が揃ったら待たずに始める
  const rnd = (a, b) => a + Math.random() * (b - a);
  const resize = (url, w) => String(url).replace(/([?&])width=\d+/, `$1width=${w}`);

  /* スクロールで浮かぶ: 枠の中で写真が静かにせり上がる */
  const initReveal = (root) => {
    if (!('IntersectionObserver' in window)) return;
    root.classList.add('clb--reveal');
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (!e.isIntersecting) return;
        e.target.classList.add('is-in');
        io.unobserve(e.target);
      });
    }, { rootMargin: '0px 0px -10% 0px' });
    root.querySelectorAll('.clb__look').forEach((el) => io.observe(el));
  };

  // 読み込めた画像の URL だけを返す(途中までしか届いていない写真を白い紙のまま出さない)
  const preload = (srcs, must) => new Promise((resolve) => {
    const ok = new Set();
    let left = srcs.length;
    const enough = Math.min(ENOUGH, srcs.length - 1);
    const check = () => {
      if (left === 0 || (ok.has(must) && ok.size - 1 >= enough)) resolve(ok);
    };
    setTimeout(() => resolve(ok), LOAD_LIMIT);
    srcs.forEach((src) => {
      const i = new Image();
      const done = (good) => { if (good) ok.add(src); left -= 1; check(); };
      i.onload = () => (i.decode ? i.decode() : Promise.resolve()).catch(() => {}).then(() => done(true));
      i.onerror = () => done(false);
      i.src = src;
    });
  });

  const runIntro = async (root, curtain) => {
    // 安全策(CSS で5秒後に消える)が先に働いた後なら、出し直さずに外す
    if (performance.now() > 4500 || getComputedStyle(curtain).visibility === 'hidden') { curtain.remove(); return; }
    curtain.style.animation = 'none';
    const mode = curtain.dataset.mode;
    const coverEl = root.querySelector('.clb__cover');
    const coverImg = coverEl && coverEl.querySelector('img');
    let looks = [];
    try {
      looks = JSON.parse(root.querySelector('[data-clb-data]').textContent).looks
        .filter((l) => l.img && l.key !== 'cover').map((l) => l.img);
    } catch (e) {
      console.error('callec-lookbook-intro: ルックのデータを読めませんでした', e);
    }
    if (!coverImg || looks.length < 3) { curtain.remove(); return; }

    const html = document.documentElement;
    const prevOverflow = html.style.overflow;
    html.style.overflow = 'hidden';
    let running = [];
    let timers = [];
    let stopped = false;
    const finish = () => {
      if (stopped) return;
      stopped = true;
      running.forEach((a) => { try { a.cancel(); } catch (e) { /* 終了済み */ } });
      timers.forEach(clearTimeout);
      curtain.remove();
      html.style.overflow = prevOverflow;
    };
    curtain.querySelector('[data-clb-skip]')?.addEventListener('click', finish);

    const wait = (ms) => new Promise((r) => timers.push(setTimeout(r, ms)));
    const anim = (el, frames, opts) => {
      const a = el.animate(frames, { fill: 'both', ...opts });
      running.push(a);
      return a.finished.catch(() => {});
    };
    const add = (markup) => { curtain.insertAdjacentHTML('beforeend', markup); return curtain.lastElementChild; };
    const flyer = (src, w, h, lift = true) =>
      add(`<div class="clb-intro__fly${lift ? ' is-lift' : ''}" style="width:${w}px;height:${h}px;transform:translate(-200vw,0)"><img src="${src}" alt=""></div>`);
    const coverRect = () => coverEl.getBoundingClientRect();
    // 本番の表紙と同じ文字を同じ位置に重ねる(最後につなぎ目なく替わるように)
    const coverText = (c, delay = 0, duration = 420) => {
      const box = add(`<div class="clb-intro__ctext" style="left:${c.left}px;top:${c.top}px;width:${c.width}px;height:${c.height}px"></div>`);
      coverEl.querySelectorAll('.clb__cover-title, .clb__cover-foot').forEach((n) => box.appendChild(n.cloneNode(true)));
      return anim(box, [{ opacity: 0 }, { opacity: 1 }], { duration, delay });
    };
    const clearGround = (ms, delay = 0) =>
      anim(curtain, [{ backgroundColor: getComputedStyle(curtain).backgroundColor }, { backgroundColor: 'rgba(0,0,0,0)' }], { duration: ms, delay, easing: 'ease' });
    const fadeOut = (ms = 420) => anim(curtain, [{ opacity: 1 }, { opacity: 0 }], { duration: ms, easing: 'ease' });

    // 表紙は srcset から選ばれた実際の URL を一度だけ決める(読み込み前後で値が変わるため)
    if (!coverImg.complete) {
      await new Promise((r) => { coverImg.addEventListener('load', r, { once: true }); coverImg.addEventListener('error', r, { once: true }); timers.push(setTimeout(r, LOAD_LIMIT)); });
      if (stopped) return;
    }
    const coverURL = coverImg.currentSrc || coverImg.src;
    const coverSrc = () => coverURL;
    // 途中の写真は 500px(めくるも1コマ0.1秒ほどなので十分。最後の表紙は本番と同じ画像)
    const want = looks.map((u) => resize(u, 500));
    const ok = await preload([coverSrc(), ...want], coverSrc());
    if (stopped) return;
    const small = want.filter((u) => ok.has(u));
    const large = small;
    if (small.length < 3 || !ok.has(coverSrc())) { finish(); return; }
    curtain.querySelector('.clb-intro__hint')?.remove();

    // 1: 舞い降りる(構図を決めたうえで少しだけ揺らす。紙らしく前後にあおられて着地)
    const playFall = async () => {
      const vw = innerWidth, vh = innerHeight;
      curtain.style.perspective = '1000px';
      const word = add(`<div class="clb-intro__word" style="top:${vh * 0.4}px"><b>${curtain.dataset.book}</b><small>${curtain.dataset.sub}</small></div>`);
      anim(word.querySelector('b'), [{ letterSpacing: '.6em', opacity: 0 }, { letterSpacing: '.14em', opacity: 1 }], { duration: 1100, easing: OUT });
      anim(word.querySelector('small'), [{ opacity: 0 }, { opacity: 1 }], { duration: 700, delay: 350 });
      const base = Math.min(vw * 0.36, 190);
      // [横位置, 縦位置, 大きさ, 傾き]。冊名の帯の上下に散らす
      const SLOTS = [[.03, .05, 1, -3], [.60, .02, .7, 4], [.36, .16, .58, -2], [.66, .25, .92, 2.5],
                     [-.02, .5, .82, 3], [.38, .58, 1, -1.5], [.72, .6, .64, -4], [.16, .8, .6, 2]];
      const order = SLOTS.map((_, i) => i).sort(() => Math.random() - .5);
      const els = SLOTS.map(([fx, fy, d, r], i) => {
        const w = base * d, h = w * 1.25;
        const x = Math.min(Math.max(fx * vw + rnd(-6, 6), -w * .15), vw - w * .85);
        const y = fy * vh + rnd(-6, 6);
        const rr = r + rnd(-1, 1);
        const el = flyer(small[i % small.length], w, h);
        el.style.zIndex = Math.round(d * 10);
        el.style.transformOrigin = '50% 0';
        el.dataset.p = JSON.stringify([x, y, rr]);
        const sway = rnd(16, 30) * (Math.random() < .5 ? -1 : 1);
        anim(el, [
          { transform: `translate3d(${x - sway}px, ${-h - 40}px, 0) rotate(${rr * 4}deg) rotateX(50deg)` },
          { transform: `translate3d(${x + sway * .5}px, ${y - h * .42}px, 0) rotate(${-rr}deg) rotateX(-16deg)`, offset: .5 },
          { transform: `translate3d(${x - sway * .12}px, ${y - 5}px, 0) rotate(${rr * 1.2}deg) rotateX(5deg)`, offset: .82 },
          { transform: `translate3d(${x}px, ${y}px, 0) rotate(${rr}deg) rotateX(0deg)` },
        ], { duration: 1250 + d * 200, delay: 240 + order[i] * 105, easing: 'cubic-bezier(.25, .75, .3, 1)' });
        return el;
      });
      await wait(240 + 105 * SLOTS.length + 1150);
      // 写真が落ちていく間に背景を透かし、本文の上を通り抜けて終わる
      anim(word, [{ opacity: 1 }, { opacity: 0 }], { duration: 380 });
      clearGround(900, 200);
      els.forEach((el, i) => {
        const [x, y, r] = JSON.parse(el.dataset.p);
        anim(el, [{ transform: `translate3d(${x}px, ${y}px, 0) rotate(${r}deg)` }, { transform: `translate3d(${x}px, ${vh + 60}px, 0) rotate(${r * 2}deg)` }],
          { duration: 900, delay: order[i] * 40, easing: IN });
      });
      await wait(40 * SLOTS.length + 900);
    };

    // 2: コマ送り(枠が上下に開き、線で進み具合。最後はピントが合うように表紙で止まる)
    const playFlip = async () => {
      const c = coverRect();
      const el = flyer(large[0], c.width, c.height, false);
      el.style.transform = `translate(${c.left}px, ${c.top}px)`;
      el.style.overflow = 'hidden';
      anim(el, [{ clipPath: 'inset(50% 0 50% 0)' }, { clipPath: 'inset(0% 0 0% 0)' }], { duration: 760, easing: INOUT });
      coverText(c, 520, 420);
      const total = Math.min(large.length, 10);
      const py = Math.min(c.bottom + 18, innerHeight - 46);
      const prog = add(`<div class="clb-intro__prog" style="left:${c.left + 16}px;width:${c.width - 32}px;top:${py}px"><span></span><em>01</em><em>${String(total).padStart(2, '0')}</em></div>`);
      anim(prog, [{ opacity: 0 }, { opacity: 1 }], { duration: 400, delay: 500 });
      const bar = prog.querySelector('span'), now = prog.querySelector('em');
      bar.style.transform = `scaleX(${1 / total})`;
      const img = el.querySelector('img');
      const seq = [...large.slice(0, total), coverSrc()];
      const gaps = [200, 150, 115, 95, 85, 85, 95, 125, 175, 250];
      await wait(900);
      for (let i = 1; i < seq.length && !stopped; i++) {
        img.src = seq[i];
        bar.style.transform = `scaleX(${Math.min((i + 1) / total, 1)})`;
        now.textContent = String(Math.min(i + 1, total)).padStart(2, '0');
        if (i === seq.length - 1) anim(img, [{ transform: 'scale(1.045)' }, { transform: 'scale(1)' }], { duration: 1200, easing: OUT });
        await wait(gaps[gaps.length - (seq.length - i)] || 120);
      }
      anim(prog, [{ opacity: 1 }, { opacity: 0 }], { duration: 400, delay: 200 });
      await wait(700);
      await fadeOut(480);
    };

    // 3: 束ねる(1枚ずつ置き、1枚ずつ重ね、一番上を持ち上げて表紙へ。広げきってから文字)
    const playGather = async () => {
      const c = coverRect(), vw = innerWidth, vh = innerHeight;
      const srcs = [...small.slice(0, 8), coverSrc()];
      const n = srcs.length, s = 0.4;
      const order = srcs.map((_, i) => i).sort(() => Math.random() - .5);
      const els = srcs.map((src, i) => {
        const el = flyer(src, c.width, c.height);
        el.style.transformOrigin = '50% 50%';
        const a = (order[i] / n) * Math.PI * 2 - Math.PI / 2 + rnd(-.18, .18);
        const rx = vw * rnd(.3, .36), ry = vh * rnd(.25, .3);
        const x = vw / 2 + Math.cos(a) * rx - c.width / 2;
        const y = vh * .46 + Math.sin(a) * ry - c.height / 2;
        el.dataset.p = JSON.stringify([x, y, rnd(-6, 6)]);
        return el;
      });
      els.forEach((el, i) => {
        const [x, y, r] = JSON.parse(el.dataset.p);
        anim(el, [
          { transform: `translate(${x}px, ${y - 10}px) scale(${s * 1.05}) rotate(${r}deg)`, opacity: 0 },
          { transform: `translate(${x}px, ${y}px) scale(${s}) rotate(${r}deg)`, opacity: 1 },
        ], { duration: 700, delay: 200 + order[i] * 90, easing: OUT });
      });
      await wait(200 + 90 * n + 560);
      els.forEach((el, i) => {
        const [x, y, r] = JSON.parse(el.dataset.p);
        const off = (n - 1 - i) * 2;
        const fr = i === n - 1 ? 0 : rnd(-2.5, 2.5);
        anim(el, [
          { transform: `translate(${x}px, ${y}px) scale(${s}) rotate(${r}deg)` },
          { transform: `translate(${c.left + off}px, ${c.top + off}px) scale(${s}) rotate(${fr}deg)` },
        ], { duration: 720, delay: i * 85, easing: INOUT });
      });
      await wait(85 * (n - 1) + 720 + 220);
      const top = els[n - 1];
      const flat = '0 1px 1px rgba(29,27,25,.05), 0 14px 34px -12px rgba(29,27,25,.28)';
      const lifted = '0 2px 2px rgba(29,27,25,.06), 0 26px 50px -14px rgba(29,27,25,.34)';
      els.slice(0, -1).forEach((el) => anim(el, [{ opacity: 1 }, { opacity: 0 }], { duration: 420, delay: 160 }));
      await anim(top, [
        { transform: `translate(${c.left}px, ${c.top}px) scale(${s})`, boxShadow: flat },
        { transform: `translate(${c.left}px, ${c.top - 6}px) scale(${s * 1.04})`, boxShadow: lifted },
      ], { duration: 380, easing: OUT });
      await anim(top, [
        { transform: `translate(${c.left}px, ${c.top - 6}px) scale(${s * 1.04})`, boxShadow: lifted },
        { transform: `translate(${c.left}px, ${c.top}px) scale(1)`, boxShadow: '0 0 0 rgba(29,27,25,0)' },
      ], { duration: 900, easing: INOUT });
      await coverText(c, 0, 420);
      await wait(160);
      await fadeOut(420);
    };

    const PLAY = { fall: playFall, flip: playFlip, gather: playGather };
    try {
      if (PLAY[mode]) await PLAY[mode]();
    } catch (e) {
      console.error('callec-lookbook-intro: 演出の再生に失敗しました', e);
    }
    finish();
  };

  const init = (root) => {
    if (root.dataset.clbIntroReady) return;
    root.dataset.clbIntroReady = '1';
    if (root.dataset.reveal === 'true') initReveal(root);
    const curtain = root.querySelector('[data-clb-intro]');
    if (curtain) runIntro(root, curtain);
  };
  // async でセクションの直後に読み込むので、この時点でセクションの HTML は揃っている
  const boot = () => document.querySelectorAll('[data-clb]').forEach(init);
  boot();
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  document.addEventListener('shopify:section:load', boot);
})();
